import {
  signIn,
  signOut,
  getCurrentUser,
  fetchUserAttributes,
  resetPassword,
  confirmResetPassword,
  confirmSignIn,
  signInWithRedirect,
  fetchAuthSession
} from 'aws-amplify/auth';
import { User, UserRole } from '../types/user';
import { isEmailAddress } from './identity';

// Amplify is configured in App.tsx, no need to configure here

export interface AuthUser {
  username: string;
  attributes: {
    email: string;
    name: string;
    'custom:employee_id': string;
    'custom:department': string;
    'custom:manager_email'?: string;
    'custom:role': string;
  };
}

export interface SignInResult {
  user: AuthUser | null;
  isSignedIn: boolean;
  nextStep?: string;
  challengeName?: string;
}

export interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

/**
 * Authentication service for managing user authentication with AWS Cognito
 */
/** Read a Cognito attribute whether it arrives flattened or as an Attributes array. */
const attrOf = (user: any, name: string): string => {
  if (!user) return '';
  const flat = user[name.replace('custom:', '')] ?? user[name];
  if (typeof flat === 'string' && flat.trim()) return flat.trim();
  const found = user.Attributes?.find((a: any) => a.Name === name)?.Value;
  return typeof found === 'string' ? found.trim() : '';
};

const isFederatedUsername = (user: any): boolean =>
  String(user?.username || user?.Username || '').startsWith('azuread_');

/** Case/whitespace-insensitive form for comparing display names. */
const normalizeName = (value?: string | null): string =>
  (value || '').trim().toLowerCase().replace(/\s+/g, ' ');

/**
 * An `azuread_<provider>_<email>` username sometimes carries the address; often the
 * trailing segment is just an opaque SAML id, in which case there is nothing to extract.
 */
export const emailFromFederatedUsername = (username?: string): string => {
  const name = username || '';
  const at = name.indexOf('@');
  if (at <= 0) return '';
  const underscore = name.lastIndexOf('_', at);
  return underscore >= 0 ? name.slice(underscore + 1).trim().toLowerCase() : '';
};

/**
 * Find the native Cognito account belonging to the same person as a federated one.
 *
 * Azure SSO creates a second account with its own sub, usually no email attribute, and
 * `custom:role` left at the default Employee — so an HR Admin signing in through SSO was
 * being treated as an ordinary employee.
 *
 * Every strategy here requires a **unique** match. The previous implementation fell back to
 * matching single words from the display name against native emails: "CreyenteOne HR Admin"
 * reduced to the word "admin" (hr being too short to survive its length filter), then took
 * the first native account whose email merely *contained* "admin" — which could be
 * creyenteone.admin@, i.e. Super Admin. It then assigned that role unconditionally. An
 * ambiguous guess here is a privilege escalation, so ambiguity now resolves to null and the
 * federated account keeps its own role.
 */
export const resolveNativeTwin = (
  candidates: any[],
  federated: { email?: string; name?: string; username?: string }
): any | null => {
  const natives = (candidates || []).filter(u => !isFederatedUsername(u));
  const uniquely = (matches: any[], strategy: string): any | null => {
    if (matches.length === 1) {
      console.log(`[Auth] native twin matched by ${strategy}`);
      return matches[0];
    }
    if (matches.length > 1) {
      console.warn(
        `[Auth] ${strategy} matched ${matches.length} native accounts — ambiguous, not elevating. ` +
        `Set custom:role on the federated account to resolve this properly.`
      );
    }
    return null;
  };

  // 1. Exact email. The only strategy that cannot be wrong.
  const email = (federated.email || '').trim().toLowerCase();
  if (email) {
    const hit = uniquely(natives.filter(u => attrOf(u, 'email').toLowerCase() === email), 'exact email');
    if (hit) return hit;
  }

  // 2. Email embedded in the azuread_ username.
  const embedded = emailFromFederatedUsername(federated.username);
  if (embedded && embedded !== email) {
    const hit = uniquely(natives.filter(u => attrOf(u, 'email').toLowerCase() === embedded), 'embedded email');
    if (hit) return hit;
  }

  // 3. Exact display name. Handles "HR Admin" on both accounts; deliberately not a
  //    substring test, which would let "Admin" match "Super Admin".
  const name = normalizeName(federated.name);
  if (name) {
    const exact = uniquely(natives.filter(u => normalizeName(attrOf(u, 'name')) === name), 'exact name');
    if (exact) return exact;

    // 3b. Azure prefixes the tenant name on display names ("CreyenteOne HR Admin"), so
    //     allow a native name that the Azure name ends with — still requiring uniqueness.
    const suffix = uniquely(
      natives.filter(u => {
        const candidate = normalizeName(attrOf(u, 'name'));
        return candidate.length >= 4 && name !== candidate && name.endsWith(` ${candidate}`);
      }),
      'tenant-prefixed name'
    );
    if (suffix) return suffix;
  }

  console.warn('[Auth] no native twin found for federated account:', federated.name || federated.username);
  return null;
};

export class AuthService {
  /**
   * The account Cognito already holds a session for, or null when there is none.
   *
   * getCurrentUser throws rather than returning null when nobody is signed in, so the throw
   * is the answer here and not a failure worth reporting.
   */
  private async currentSessionUser(): Promise<{ username: string; email: string } | null> {
    try {
      const current = await getCurrentUser();
      let email = '';
      try {
        email = String((await fetchUserAttributes()).email || '');
      } catch {
        // A live session whose attributes could not be read. The username still answers
        // "is this the same person", so a failed attribute read must not be reported as
        // "no session" — that would send us straight back into signIn to throw again.
      }
      return { username: String(current?.username || ''), email };
    } catch {
      return null;
    }
  }

  /** Whether a live session belongs to the account being asked for. */
  private sameAccount(session: { username: string; email: string }, requested: string): boolean {
    const want = requested.trim().toLowerCase();
    if (!want) return false;
    return (
      session.email.trim().toLowerCase() === want ||
      session.username.trim().toLowerCase() === want
    );
  }

  /** Signing the old session out must never be the thing that fails a login. */
  private async safeSignOut(): Promise<void> {
    try {
      await signOut();
    } catch (err) {
      console.warn('[Auth] Could not sign the previous session out cleanly:', err);
    }
  }

  /** A SignInResult built from whatever session is currently live. */
  private async resultFromSession(fallbackEmail = ''): Promise<SignInResult> {
    const user = await getCurrentUser();
    const attributes = await fetchUserAttributes();

    console.log('[Auth] User attributes:', attributes);

    return {
      user: {
        username: user.username,
        attributes: {
          email: attributes.email || fallbackEmail,
          name: attributes.name || attributes['custom:name'] || '',
          'custom:employee_id': attributes['custom:employeeId'] || attributes['custom:employee_id'] || '',
          'custom:department': attributes['custom:department'] || '',
          'custom:manager_email': attributes['custom:managerEmail'] || attributes['custom:manager_email'],
          'custom:role': attributes['custom:role'] || UserRole.EMPLOYEE
        }
      },
      isSignedIn: true
    };
  }

  /** The raw Cognito sign-in, with no session-conflict handling — see signIn for that. */
  private async attemptSignIn(email: string, password: string): Promise<SignInResult> {
    const result = await signIn({
      username: email,
      password: password
    });

    console.log('[Auth] Sign in result:', result);

    // Check if password change is required
    if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
      console.log('[Auth] New password required');
      return {
        user: null,
        isSignedIn: false,
        nextStep: 'NEW_PASSWORD_REQUIRED',
        challengeName: 'NEW_PASSWORD_REQUIRED'
      };
    }

    if (result.isSignedIn) {
      return this.resultFromSession(email);
    }

    return {
      user: null,
      isSignedIn: false,
      nextStep: result.nextStep?.signInStep
    };
  }

  /**
   * Sign in user with email and password.
   *
   * Amplify throws `UserAlreadyAuthenticatedException` ("There is already a signed in user")
   * if a session is still live when signIn is called, and Cognito tokens survive in
   * localStorage across refreshes and tab closes. The login form is fully interactive while
   * AuthContext is still restoring that session — `authLoading` gates only the redirect, not
   * the form — so submitting during that window threw, the exception was rendered as a red
   * login error, and then the restore completed and redirected to /home anyway. An error
   * immediately followed by a success is the worst of both: the login works but reads as
   * broken, which is exactly what it looked like.
   *
   * Resolved before calling signIn rather than only in the catch, so the ordinary case does
   * not spend a failed auth attempt to discover something we could have checked.
   */
  async signIn(email: string, password: string): Promise<SignInResult> {
    console.log('[Auth] Attempting sign in for:', email);

    const existing = await this.currentSessionUser();
    if (existing) {
      if (this.sameAccount(existing, email)) {
        // Already signed in as precisely the account being requested. Report the session
        // rather than tearing it down and rebuilding it to reach the same place.
        console.log('[Auth] Session already open for this account; reusing it');
        return this.resultFromSession(email);
      }
      /*
       * A different account holds the session, so it has to go. Reusing it would sign the
       * visitor in as somebody else — the previous user's role and data behind the new
       * user's credentials — which is the one outcome here that is worse than an error.
       */
      console.log('[Auth] Clearing a different account before sign in:', existing.email || existing.username);
      await this.safeSignOut();
    }

    try {
      return await this.attemptSignIn(email, password);
    } catch (error: any) {
      /*
       * The check above cannot close the race entirely: AuthContext can finish restoring a
       * session in the moment between that check and this call. Same resolution, once —
       * a single retry, never a loop, so a genuinely stuck session surfaces as an error
       * instead of spinning.
       */
      if (error?.name === 'UserAlreadyAuthenticatedException') {
        console.warn('[Auth] Session appeared mid-sign-in; resolving against it');
        const live = await this.currentSessionUser();
        if (live && this.sameAccount(live, email)) {
          return this.resultFromSession(email);
        }
        await this.safeSignOut();
        return await this.attemptSignIn(email, password);
      }
      console.error('[Auth] Sign in error:', error);
      throw error;
    }
  }

  /**
   * Complete new password challenge
   */
  async completeNewPassword(newPassword: string): Promise<SignInResult> {
    try {
      console.log('[Auth] Completing new password challenge');
      const result = await confirmSignIn({
        challengeResponse: newPassword
      });

      console.log('[Auth] Complete new password result:', result);

      if (result.isSignedIn) {
        const user = await getCurrentUser();
        const attributes = await fetchUserAttributes();

        return {
          user: {
            username: user.username,
            attributes: {
              email: attributes.email || '',
              name: attributes.name || attributes['custom:name'] || '',
              'custom:employee_id': attributes['custom:employeeId'] || attributes['custom:employee_id'] || '',
              'custom:department': attributes['custom:department'] || '',
              'custom:manager_email': attributes['custom:managerEmail'] || attributes['custom:manager_email'],
              'custom:role': attributes['custom:role'] || UserRole.EMPLOYEE
            }
          },
          isSignedIn: true
        };
      }

      throw new Error('Failed to complete password change');
    } catch (error) {
      console.error('[Auth] Complete new password error:', error);
      throw error;
    }
  }

  /**
   * Sign out current user
   */
  async signOut(): Promise<void> {
    try {
      await signOut();
    } catch (error) {
      console.error('[Auth] Sign out error:', error);
      throw error;
    }
  }

  /**
   * Force-refresh the Cognito session and report whether it is still valid.
   *
   * Used by the "Stay Signed In" action on the inactivity warning: it proves the session is
   * genuinely alive (and rotates the tokens) via the Amplify-supported forceRefresh, rather than
   * only resetting a client-side timer. Returns false when no valid session remains, so the
   * caller can sign the user out honestly instead of claiming an extension that did not happen.
   */
  async refreshSession(): Promise<boolean> {
    try {
      const session = await fetchAuthSession({ forceRefresh: true });
      return !!session.tokens;
    } catch (error) {
      console.warn('[Auth] Session refresh failed:', error);
      return false;
    }
  }

  /**
   * Get current authenticated user
   */
  async getCurrentUser(): Promise<User | null> {
    try {
      // First check if there's a valid session
      const session = await fetchAuthSession();
      if (!session.tokens) {
        console.log('[Auth] No valid session found');
        return null;
      }

      const user = await getCurrentUser();
      const attributes = await fetchUserAttributes();
      
      // Also get ID token payload for federated users (custom attributes are there)
      const idTokenPayload = session.tokens?.idToken?.payload;

      console.log('[Auth] Raw user attributes:', JSON.stringify(attributes, null, 2));
      console.log('[Auth] All attribute keys:', Object.keys(attributes));
      console.log('[Auth] ID Token payload:', JSON.stringify(idTokenPayload, null, 2));
      console.log('[Auth] Role from attributes:', attributes['custom:role']);
      console.log('[Auth] Role from ID token:', idTokenPayload?.['custom:role']);

      // Try to get role from attributes first, then from ID token payload
      let roleAttr = (
        attributes['custom:role'] || 
        idTokenPayload?.['custom:role'] as string
      )?.trim() || '';

      // Get email for DynamoDB lookup
      // For federated users, email might not be in attributes or token
      let email = attributes.email || idTokenPayload?.email as string || '';
      
      // For federated (SSO) users: if email is empty, try to get it from Cognito Admin API
      const cognitoUsername = idTokenPayload?.['cognito:username'] as string || '';
      const isFederated = cognitoUsername.startsWith('azuread_') || !!(idTokenPayload?.identities);

      /*
       * Recovery runs for **any** account with no email, not only federated ones.
       *
       * The gate used to be `!email && isFederated`, on the assumption that a native account
       * always presents its address in the token. It does not have to: the address lives on the
       * Cognito user, and whether it reaches the client depends on the app client's read
       * attributes and requested scopes. An account whose `email` is simply not readable through
       * the token therefore fell straight past this block, past the username fallback below
       * (Cognito usernames are frequently a UUID, not an address), and arrived at the UI with
       * `email: ''` — which every per-employee read in the Leave module treats as "no records",
       * and which is what puts the "This account has no email address on it" notice on Apply
       * Comp-Off. The account has one; this session just never asked the one API that has it.
       *
       * The self-match added below is exact and therefore cannot mis-identify anybody, so
       * running it for native accounts costs one request on a path that only executes when the
       * session genuinely could not name the user.
       */
      if (!email) {
        console.log(
          `[Auth] No email in attributes or token (${isFederated ? 'federated' : 'native'} account ` +
          `"${cognitoUsername || user.username}"). Looking up via Admin API...`
        );
        const ADMIN_API_ENDPOINT = process.env.REACT_APP_ADMIN_API_ENDPOINT;
        if (ADMIN_API_ENDPOINT) {
          try {
            const token = session.tokens?.idToken?.toString();
            const response = await fetch(`${ADMIN_API_ENDPOINT}/users`, {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': token ? `Bearer ${token}` : ''
              }
            });
            if (response.ok) {
              const data = await response.json();
              const allUsers = data.users || [];

              /*
               * Step 0: this account's own row, matched on Cognito username.
               *
               * Tried before any twin resolution because it is the only strategy that identifies
               * the person rather than inferring them: the username in the token and the username
               * on the row are the same string for the same account. It is also the case the
               * twin logic below cannot help with at all — a native account has no twin to
               * resolve, so without this there was nothing left to try.
               */
              const ownRow = allUsers.find((u: any) => {
                const rowUsername = String(u.username || u.Username || '');
                return rowUsername && rowUsername === (cognitoUsername || user.username);
              });
              const ownEmail = attrOf(ownRow, 'email');
              if (ownEmail) {
                email = ownEmail;
                console.log('[Auth] Recovered email from this account\'s own Cognito record:', email);
              }

              // Step 1: Find the federated user to get their email
              const federatedUser = allUsers.find((u: any) =>
                (u.username || u.Username) === cognitoUsername
              );
              
              let federatedEmail = '';
              if (federatedUser) {
                federatedEmail = federatedUser.email || 
                  federatedUser.Attributes?.find((a: any) => a.Name === 'email')?.Value || '';
                console.log('[Auth] Federated user email:', federatedEmail);
              }
              
              // Step 2: Find the NATIVE user with the same email (they have the correct role)
              // Native users are the ones WITHOUT "azuread_" prefix
              if (federatedEmail) {
                const nativeUser = allUsers.find((u: any) => {
                  const uUsername = u.username || u.Username || '';
                  const uEmail = (u.email || u.Attributes?.find((a: any) => a.Name === 'email')?.Value || '').toLowerCase();
                  return !uUsername.startsWith('azuread_') && uEmail === federatedEmail.toLowerCase();
                });
                
                if (nativeUser) {
                  const nativeRole = nativeUser.role || 
                    nativeUser.Attributes?.find((a: any) => a.Name === 'custom:role')?.Value || '';
                  const nativeName = nativeUser.name || 
                    nativeUser.Attributes?.find((a: any) => a.Name === 'name')?.Value || '';
                  
                  email = federatedEmail;
                  if (nativeRole && nativeRole !== 'Employee') {
                    roleAttr = nativeRole;
                    console.log('[Auth] Found role from native user:', roleAttr);
                  }
                  if (nativeName) {
                    (attributes as any).name = nativeName;
                    console.log('[Auth] Found name from native user:', nativeName);
                  }
                } else {
                  // No native user found, use federated user's data
                  email = federatedEmail;
                  const fedRole = federatedUser.role || 
                    federatedUser.Attributes?.find((a: any) => a.Name === 'custom:role')?.Value || '';
                  if (fedRole && fedRole !== 'Employee') {
                    roleAttr = fedRole;
                  }
                  console.log('[Auth] No native user found, using federated data. Email:', email, 'Role:', roleAttr);
                }
              } else if (isFederated) {
                // No email on the federated account — the common case, and the reason this
                // whole block exists. Resolve the twin by unique match rather than guesswork.
                //
                // Federated only, deliberately. Twin resolution ends in a display-name match,
                // which infers who somebody is rather than establishing it, and it assigns that
                // account's role — so it is only justified where there genuinely are two
                // accounts for one person. A native account has no twin to find, and letting it
                // adopt a same-named account's identity would be a guess with privileges
                // attached. Native accounts get the exact username self-match in Step 0 and
                // nothing weaker.
                console.log('[Auth] No email on federated user, resolving native twin...');
                const twin = resolveNativeTwin(allUsers, {
                  name: (attributes as any).name || (idTokenPayload?.name as string) || '',
                  username: cognitoUsername,
                });
                if (twin) {
                  const twinEmail = attrOf(twin, 'email');
                  const twinRole = attrOf(twin, 'custom:role') || twin.role || '';
                  const twinName = attrOf(twin, 'name');
                  if (twinEmail) email = twinEmail;
                  if (twinRole && twinRole !== 'Employee') roleAttr = twinRole;
                  if (twinName) (attributes as any).name = twinName;
                  console.log('[Auth] Resolved via native twin. Email:', email, 'Role:', roleAttr);
                }
              }
            }
          } catch (err) {
            console.warn('[Auth] Failed to look up the signed-in account via the Admin API:', err);
          }
        } else {
          // Worth saying out loud: with no endpoint configured there is no way to recover an
          // address the token did not carry, so the session will run without one and every
          // per-employee screen will read as empty.
          console.warn(
            '[Auth] REACT_APP_ADMIN_API_ENDPOINT is not set, so the missing email cannot be ' +
            'recovered. Per-employee data (leave balance, Comp-Off, overtime) will read as empty.'
          );
        }
      }
      
      /*
       * Last resort: the Cognito username, when it is itself an email.
       *
       * Native accounts are created with the email as the username, so this recovers the
       * identity of an account that simply has no `email` attribute set — which the
       * bootstrapped Super Admin did not. It matters far beyond the display name: every
       * per-employee read in the Leave module is keyed on this address, and an empty one
       * used to switch those filters off rather than return nothing, so the account was
       * shown Comp-Off and HR adjustments belonging to the whole company.
       *
       * Deliberately not falling back to `user.userId` or an `azuread_…` username. Those
       * identify the account but not the person, and writing rows against one would file
       * an employee's leave history under a key nothing else looks up.
       */
      if (!email) {
        const usernameCandidate = (user.username || cognitoUsername || '').trim();
        if (isEmailAddress(usernameCandidate)) {
          email = usernameCandidate;
          console.log('[Auth] Email absent from attributes and token; using username:', email);
        } else {
          // Names the account, because the fix is an edit to one specific Cognito user and
          // "some account has no email" does not tell an admin which one to open.
          console.warn(
            '[Auth] Could not resolve an email for this account. Per-employee data ' +
            '(leave balance, Comp-Off, overtime) cannot be identified and will read as empty. ' +
            'Set the email attribute on the Cognito user to fix this.',
            { username: user.username, cognitoUsername, sub: user.userId, federated: isFederated }
          );
        }
      }

      console.log('[Auth] Initial roleAttr:', JSON.stringify(roleAttr), 'email:', email);

      // If role not in token or is empty or is default Employee, fetch from Cognito Admin API
      if ((!roleAttr || roleAttr === 'Employee' || roleAttr === 'EMPLOYEE') && email) {
        console.log('[Auth] Role not in token, fetching from Cognito Admin API for:', email);
        const ADMIN_API_ENDPOINT = process.env.REACT_APP_ADMIN_API_ENDPOINT;
        if (!ADMIN_API_ENDPOINT) {
          console.warn('[Auth] REACT_APP_ADMIN_API_ENDPOINT not set, cannot fetch role');
        } else {
        try {
          const token = session.tokens?.idToken?.toString();
          
          const response = await fetch(`${ADMIN_API_ENDPOINT}/users`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': token ? `Bearer ${token}` : ''
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            console.log('[Auth] Cognito Admin API response:', data);
            
            const allUsers = data.users || [];
            console.log('[Auth] All users from Cognito:', allUsers.length, 'users');
            
            // Find user by email - prefer native user with role over federated user
            const matchingUsers = allUsers.filter((u: any) => {
              const userEmail = u.email || u.Attributes?.find((a: any) => a.Name === 'email')?.Value;
              return userEmail?.toLowerCase() === email.toLowerCase();
            });
            
            console.log('[Auth] Matching users for email:', matchingUsers.length);
            
            // Sort: prefer users with role set (not Employee default) and native users over federated
            const cognitoUser = matchingUsers.sort((a: any, b: any) => {
              // Prefer user with non-Employee role
              const aHasRole = a.role && a.role !== 'Employee';
              const bHasRole = b.role && b.role !== 'Employee';
              if (aHasRole && !bHasRole) return -1;
              if (!aHasRole && bHasRole) return 1;
              
              // Prefer native user over federated (azuread_)
              const aIsFederated = a.username?.startsWith('azuread_');
              const bIsFederated = b.username?.startsWith('azuread_');
              if (!aIsFederated && bIsFederated) return -1;
              if (aIsFederated && !bIsFederated) return 1;
              
              return 0;
            })[0];
            
            console.log('[Auth] Selected Cognito user:', cognitoUser);
            
            if (cognitoUser) {
              // Get role from attributes
              const userRole = cognitoUser.role || 
                cognitoUser.Attributes?.find((a: any) => a.Name === 'custom:role')?.Value;
              
              if (userRole) {
                console.log('[Auth] Found role in Cognito:', userRole);
                roleAttr = userRole;
              }
            }
          } else {
            console.warn('[Auth] Cognito Admin API returned:', response.status);
          }
        } catch (apiError) {
          console.warn('[Auth] Could not fetch role from Cognito Admin API:', apiError);
        }
        } // end if ADMIN_API_ENDPOINT
      }

      // If still no role or still Employee, try GraphQL listUsers as final fallback
      // ALWAYS check DynamoDB as source of truth for role (Settings page writes here)
      if (email) {
        console.log('[Auth] Checking DynamoDB for authoritative role for:', email);
        try {
          const { generateClient } = await import('aws-amplify/api');
          const gqlClient = generateClient();
          const listUsersQuery = `query ListUsers($limit: Int) { listUsers(limit: $limit) { items { id email name role } } }`;
          const result = await gqlClient.graphql({ query: listUsersQuery, variables: { limit: 200 }, authMode: 'userPool' } as any) as any;
          const dbUsers = result.data?.listUsers?.items || [];
          const dbUser = dbUsers.find((u: any) => (u.email || '').toLowerCase() === email.toLowerCase());
          if (dbUser?.role) {
            console.log('[Auth] Found role in DynamoDB:', dbUser.role);
            roleAttr = dbUser.role;
          }
        } catch (gqlErr) {
          console.warn('[Auth] GraphQL listUsers fallback failed:', gqlErr);
        }
      }

      // FINAL OVERRIDE: Always check DynamoDB User table for authoritative role
      // This ensures the role set by Super Admin in Settings always takes priority
      // For federated users without email, match by name
      // Uses Admin API (not GraphQL) to avoid permission issues
      if (isFederated && (!roleAttr || roleAttr === 'Employee' || roleAttr === 'EMPLOYEE')) {
        console.log('[Auth] FINAL OVERRIDE - attempting Admin API lookup for federated user');
        const ADMIN_API_ENDPOINT = process.env.REACT_APP_ADMIN_API_ENDPOINT;
        if (ADMIN_API_ENDPOINT) {
          try {
            const token = session.tokens?.idToken?.toString();
            const response = await fetch(`${ADMIN_API_ENDPOINT}/users`, {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': token ? `Bearer ${token}` : ''
              }
            });
            if (response.ok) {
              const data = await response.json();
              const allCognitoUsers = data.users || [];
              // Same unique-match resolution as above. The previous Strategy 1/2 chain
              // guessed at "creyenteone.<words>@" email prefixes and then matched single
              // words from the display name against native emails, which could hand an HR
              // Admin the Super Admin role.
              const matchedUser = resolveNativeTwin(allCognitoUsers, {
                email,
                name: (attributes as any).name || (idTokenPayload?.name as string) || '',
                username: cognitoUsername,
              });
              
              if (matchedUser) {
                const mRole = matchedUser.role || matchedUser.Attributes?.find((a: any) => a.Name === 'custom:role')?.Value || '';
                const mEmail = matchedUser.email || matchedUser.Attributes?.find((a: any) => a.Name === 'email')?.Value || '';
                const mName = matchedUser.name || matchedUser.Attributes?.find((a: any) => a.Name === 'name')?.Value || '';
                
                console.log('[Auth] FINAL OVERRIDE - matched user:', mName, mEmail, mRole);
                
                if (mRole) {
                  roleAttr = mRole;
                  console.log('[Auth] FINAL OVERRIDE - matched role:', roleAttr);
                }
                if (mEmail) {
                  email = mEmail;
                  console.log('[Auth] FINAL OVERRIDE - matched email:', email);
                }
                if (mName) {
                  (attributes as any).name = mName;
                  console.log('[Auth] FINAL OVERRIDE - matched name:', mName);
                }
              } else {
                console.warn(
                  '[Auth] FINAL OVERRIDE - no native twin resolved; this account keeps role',
                  roleAttr || 'Employee',
                  '— set custom:role on the federated Cognito account to fix this permanently.'
                );
              }
            }
          } catch (err) {
            console.warn('[Auth] FINAL OVERRIDE failed:', err);
          }
        }
      }

      // Map role string to UserRole enum (case-insensitive)
      let role = UserRole.EMPLOYEE;
      const roleUpper = roleAttr?.toUpperCase();
      
      console.log('[Auth] Role mapping - raw:', roleAttr, 'upper:', roleUpper);
      
      if (roleUpper === 'MANAGER' || roleAttr === 'Manager') {
        role = UserRole.MANAGER;
      } else if (roleUpper === 'FINANCE_ADMIN' || roleAttr === 'Finance_Admin') {
        role = UserRole.FINANCE_ADMIN;
      } else if (roleUpper === 'HR_ADMIN' || roleAttr === 'HR_Admin' || roleUpper === 'HRADMIN') {
        role = UserRole.HR_ADMIN;
      } else if (roleUpper === 'SUPER_ADMIN' || roleAttr === 'Super_Admin') {
        role = UserRole.SUPER_ADMIN;
      } else if (roleUpper === 'EMPLOYEE' || roleAttr === 'Employee') {
        role = UserRole.EMPLOYEE;
      }
      
      console.log('[Auth] Final mapped role:', role);

      // Get other attributes from both sources (email already defined above)
      const name = attributes.name || attributes['custom:name'] || idTokenPayload?.name as string || '';
      const employeeId = attributes['custom:employeeId'] || attributes['custom:employee_id'] || idTokenPayload?.['custom:employeeId'] as string || '';
      const department = attributes['custom:department'] || idTokenPayload?.['custom:department'] as string || '';
      // Check multiple possible attribute names for managerEmail.
      // managerEmail is optional on the User type, so fall back to undefined
      // (not '') when it's genuinely absent — an empty string would incorrectly
      // read as "has a manager email" to callers doing truthiness checks.
      const managerEmail = attributes['custom:managerEmail'] ||
                          attributes['custom:manager_email'] ||
                          attributes['managerEmail'] ||
                          idTokenPayload?.['custom:managerEmail'] as string ||
                          idTokenPayload?.['custom:manager_email'] as string ||
                          idTokenPayload?.['managerEmail'] as string ||
                          undefined;
      
      console.log('[Auth] Manager email extraction:', {
        'custom:managerEmail': attributes['custom:managerEmail'],
        'custom:manager_email': attributes['custom:manager_email'],
        'managerEmail': attributes['managerEmail'],
        'idToken custom:managerEmail': idTokenPayload?.['custom:managerEmail'],
        'final managerEmail': managerEmail
      });

      const userData = {
        id: user.userId,
        email,
        name,
        employeeId,
        department,
        managerEmail,
        role: role,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      console.log('[Auth] Parsed user data:', userData);
      return userData;
    } catch (error: any) {
      // Handle specific authentication errors
      if (error.name === 'UserUnAuthenticatedException' || error.message?.includes('not authenticated')) {
        console.log('[Auth] User is not authenticated');
        return null;
      }
      
      console.error('[Auth] Get current user error:', error);
      return null;
    }
  }

  /**
   * Reset user password
   */
  async resetPassword(email: string): Promise<void> {
    try {
      await resetPassword({ username: email });
    } catch (error) {
      console.error('[Auth] Reset password error:', error);
      throw error;
    }
  }

  /**
   * Confirm password reset with code
   */
  async confirmResetPassword(email: string, code: string, newPassword: string): Promise<void> {
    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword: newPassword
      });
    } catch (error) {
      console.error('[Auth] Confirm reset password error:', error);
      throw error;
    }
  }

  /**
   * Check if user is authenticated
   */
  async isAuthenticated(): Promise<boolean> {
    try {
      await getCurrentUser();
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Check if user has required role
   */
  hasRole(user: User | null, requiredRoles: UserRole[]): boolean {
    if (!user) return false;
    return requiredRoles.includes(user.role);
  }

  /**
   * Sign in with Azure AD (federated sign-in)
   */
  async signInWithAzureAD(): Promise<void> {
    try {
      console.log('[Auth] Initiating Azure AD sign-in');
      console.log('[Auth] Current window.location:', window.location.href);
      console.log('[Auth] Provider config:', { custom: 'AzureAD' });
      
      await signInWithRedirect({
        provider: { custom: 'AzureAD' }
      });
      
      console.log('[Auth] signInWithRedirect called successfully');
    } catch (error) {
      console.error('[Auth] Azure AD sign-in error:', error);
      throw error;
    }
  }

  /**
   * Check if current session is from federated provider
   */
  async isFederatedSession(): Promise<boolean> {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken;
      if (idToken) {
        const payload = idToken.payload;
        // Check if identity came from external provider
        return !!(payload['identities'] || payload['cognito:username']?.toString().includes('AzureAD'));
      }
      return false;
    } catch {
      return false;
    }
  }
}